import React from 'react';
import { Link } from 'react-router-dom';
import { X, Zap, Clock, ArrowUpRight } from 'lucide-react';
import { RateLimitInfo } from '../utils/errorHandler';

interface RateLimitNotificationProps {
  rateLimitInfo: RateLimitInfo;
  feature?: string;
  onClose?: () => void;
}

const FEATURE_NAMES: Record<string, string> = {
  'cv_search': 'Passport Search',
  'creator_search': 'Creator Search',
  'company_search': 'Company Search',
  'candidate_search': 'Candidate Search',
  'candidate_analysis': 'AI Analysis',
  'vee_chat': 'Vee AI',
  'reach_outs': 'Reach-outs',
  'ats_push': 'ATS Push',
  'cv_upload': 'CV Upload & ATS Readability Analysis',
  'mock_simulations': 'AI Interview Simulations',
};

const SUBSCRIPTION_NAMES: Record<string, string> = {
  'guest': 'Guest',
  'explorer_free': 'Explorer Free',
  'explorer_premium': 'Explorer Premium',
  'recruiter_free': 'Recruiter Free',
  'recruiter_premium': 'Recruiter Premium'
};

const RateLimitNotification: React.FC<RateLimitNotificationProps> = ({ 
  rateLimitInfo, 
  feature,
  onClose
}) => {
  const isMonthly = rateLimitInfo.type === 'subscription_limit';
  const featureName = feature ? FEATURE_NAMES[feature] || feature : 'this feature';
  const subscriptionName = SUBSCRIPTION_NAMES[rateLimitInfo.subscription_type] || rateLimitInfo.subscription_type || 'Unknown';
  const isRecruiter = rateLimitInfo.subscription_type?.includes('recruiter') || false;
  const profileLink = isRecruiter ? '/recruiter-profile' : '/profile';
  
  return (
    <div className="fixed inset-x-0 top-0 sm:inset-auto sm:top-4 sm:right-4 z-[10001] sm:max-w-sm animate-slide-in">
      <div className="bg-white border border-slate-200 sm:rounded-2xl shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)] overflow-hidden">
        <div className="px-4 py-3 flex items-start justify-between border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
              {isMonthly ? <Zap className="w-4 h-4 text-slate-600" /> : <Clock className="w-4 h-4 text-slate-600" />}
            </div>
            <div>
              <h3 className="text-sm font-semibold tracking-tight text-slate-900">
                {isMonthly ? 'Limit erreicht' : 'Zu viele Anfragen'}
              </h3>
              <p className="text-[11px] text-slate-400">
                {isMonthly ? 'Monatliches Limit' : 'Bitte kurz warten'}
              </p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-lg transition-colors">
              <X className="w-4 h-4 text-slate-400" />
            </button>
          )}
        </div>
        
        <div className="px-4 py-3 space-y-3">
          {isMonthly ? (
            <>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">{featureName}</span>
                  <span className="font-semibold text-slate-700">
                    {rateLimitInfo.feature_used}/{rateLimitInfo.feature_limit}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div 
                    className="bg-pink-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (rateLimitInfo.feature_used / rateLimitInfo.feature_limit) * 100)}%` }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-500">Gesamt</span>
                  <span className="font-semibold text-slate-700">
                    {rateLimitInfo.global_used}/{rateLimitInfo.global_limit}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div 
                    className="bg-slate-400 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (rateLimitInfo.global_used / rateLimitInfo.global_limit) * 100)}%` }}
                  />
                </div>
              </div>

              <p className="text-[11px] text-slate-400">
                Plan: <span className="font-medium text-slate-500">{subscriptionName}</span>
              </p>
            </>
          ) : (
            <p className="text-xs text-slate-500 leading-relaxed">
              Zu viele Anfragen von deiner IP-Adresse. Bitte warte ca. <span className="font-semibold text-slate-700">1 Stunde</span>.
            </p>
          )}
        </div>
        
        {isMonthly && (
          <div className="px-4 py-3 bg-slate-50/70 border-t border-slate-100 flex gap-2">
            <Link
              to="/pricing"
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 rounded-lg shadow-sm shadow-pink-500/20 transition-all"
            >
              <Zap className="w-3 h-3" /> Upgrade
            </Link>
            <Link
              to={profileLink}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors"
            >
              Usage <ArrowUpRight className="w-3 h-3" />
            </Link>
            {onClose && (
              <button
                onClick={onClose}
                className="px-3 py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                OK
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RateLimitNotification;
